import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaFacebook, FaTwitter, FaLinkedin, FaSpinner, FaCheckCircle, FaQuestionCircle, FaBookOpen, FaShieldAlt, FaFileAlt } from 'react-icons/fa'; // More Icons
import axiosPublic from '../../api/axiosPublic';
import { Link } from 'react-router-dom';

/**
 * Enhanced Footer Component with:
 * - Comprehensive Company info
 * - Detailed Navigation links (Product, Resources, Company, Legal)
 * - Improved and accessible contact form with feedback (spinner & success)
 * - Prominent Social icons
 * - Focus on better UI and UX for a school administration app
 */

// --- Reusable Keyframes ---
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// --- Styled Components ---
const FooterWrapper = styled.footer`
  background: linear-gradient(160deg, #0f172a 0%, #1f2937 58%, #111827 100%);
  color: #d0d3d9;
  padding: 4rem 2rem; /* Increased padding */
  border-top: 1px solid #2d3748; /* Subtle border */

  @media (min-width: 768px) {
    padding: 5rem 3rem;
  }
  @media (min-width: 992px) {
    padding: 6rem 4rem;
  }
`;

const InnerContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  gap: 3rem; /* Increased gap */
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); /* Wider columns on larger screens */
  }
  @media (min-width: 992px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); /* Even wider on desktop */
  }
`;

const Column = styled.div`
  font-size: 1rem;
`;

const Title = styled.h4`
  font-size: 1.25rem; /* Slightly larger title */
  font-weight: 600; /* Slightly lighter font weight for titles */
  color: #fff;
  margin-bottom: 1rem; /* Increased margin bottom */
  border-bottom: 2px solid #38bdf8; /* Accent color underline */
  padding-bottom: 0.5rem;
  display: inline-block; /* Underline only as wide as text */
`;

const Description = styled.p`
  font-size: 0.95rem; /* Slightly larger description font */
  color: #a3a6b1;
  margin-bottom: 1rem; /* Increased margin bottom */
  line-height: 1.7; /* Improved line height for readability */
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ListItem = styled.li`
  margin-bottom: 0.6rem; /* Increased list item margin */
`;

const FooterLink = styled.a`
  text-decoration: none;
  color: #d0d3d9;
  display: flex; /* Use flex to align icon and text */
  align-items: center;
  transition: color 0.2s ease-in-out, transform 0.2s ease-in-out; /* Added transform transition */
  font-family: 'Inter', 'Manrope', 'Plus Jakarta Sans', sans-serif;
  font-size: 0.95rem; /* Slightly larger link font */
  padding: 0.2rem 0; /* Vertical padding for easier clicking */

  &:hover {
    color: #38bdf8; /* Accent color hover */
    transform: translateX(5px); /* Subtle hover animation */
  }
`;

const LinkIcon = styled.span`
  margin-right: 0.6rem; /* Spacing between icon and text */
  color: #777; /* Muted icon color */
  font-size: 1rem;
  display: inline-flex; /* Ensure icon and text align vertically */
  align-items: center;
`;


const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem; /* Increased gap in form */
  margin-top: 0.75rem;
`;

const Label = styled.label`
  font-size: 1rem; /* Slightly larger label font */
  color: #fff;
  margin-bottom: 0.3rem;
  font-family: 'Inter', 'Manrope', 'Plus Jakarta Sans', sans-serif;
  font-weight: 500; /* Slightly bolder label */
`;

const Input = styled.input`
  padding: 0.85rem; /* Slightly larger input padding */
  border-radius: 6px; /* More rounded inputs */
  border: 1px solid #444;
  background-color: #2d3748; /* Darker input background */
  color: #eee;
  font-family: 'Inter', 'Manrope', 'Plus Jakarta Sans', sans-serif;
  font-size: 1rem; /* Larger input font */
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:focus {
    outline: none;
    border-color: #00ef9a; /* Accent color focus border */
    box-shadow: 0 0 0 2px rgba(0, 239, 154, 0.25); /* Accent color focus shadow */
  }
`;

const Textarea = styled.textarea`
  padding: 0.85rem; /* Slightly larger textarea padding */
  border-radius: 6px; /* More rounded textarea */
  border: 1px solid #444;
  background-color: #2d3748; /* Darker textarea background */
  color: #eee;
  font-family: 'Inter', 'Manrope', 'Plus Jakarta Sans', sans-serif;
  font-size: 1rem; /* Larger textarea font */
  resize: vertical;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:focus {
    outline: none;
    border-color: #00ef9a; /* Accent color focus border */
    box-shadow: 0 0 0 2px rgba(0, 239, 154, 0.25); /* Accent color focus shadow */
  }
`;

const SubmitButton = styled.button`
  padding: 0.85rem 2rem; /* Larger submit button padding */
  border-radius: 6px; /* More rounded button */
  border: none;
  background-color: #38bdf8; /* Accent color button */
  color: #1e273a; /* Dark text on button */
  font-weight: 600;
  font-family: 'Inter', 'Manrope', 'Plus Jakarta Sans', sans-serif;
  font-size: 1.1rem; /* Larger button font */
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, transform 0.2s ease-in-out; /* Added transform transition */
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #0ea5e9; /* Darker accent color on hover */
    transform: translateY(-2px); /* Subtle hover lift */
  }
  &:active {
    transform: translateY(0px); /* Reset lift on active */
  }
  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const FormStatus = styled.p`
  margin-top: 0.75rem; /* Increased margin top for status message */
  font-size: 0.9rem;
  color: ${({ error }) => (error ? '#ff6b6b' : '#38bdf8')}; /* Accent color for success */
  display: flex; /* Center icon and text vertically */
  align-items: center;
  justify-content: center;
`;

const StatusIcon = styled.span`
  margin-right: 0.5rem;
  font-size: 1.1rem;
`;


const SocialIconsWrapper = styled.div`
  margin-top: 1.5rem; /* Increased margin top for social icons */
  display: flex;
  justify-content: flex-start; /* Align icons to the start */
`;

const SocialIconLink = styled.a`
  margin-right: 0.9rem; /* Increased spacing between social icons */
  color: #d0d3d9;
  text-decoration: none;
  background: #2d3748; /* Darker social icon background */
  padding: 0.6rem 0.85rem; /* Larger padding for icons */
  border-radius: 8px; /* More rounded social icons */
  font-size: 1.1rem; /* Larger social icons */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem; /* Fixed width and height for icons */
  height: 2.5rem;
  transition: background-color 0.2s ease-in-out, transform 0.2s ease-in-out, color 0.2s ease-in-out; /* Added color transition */

  &:hover {
    background-color: #00ef9a; /* Accent color on hover */
    transform: translateY(-3px) scale(1.1); /* More pronounced hover effect */
    color: #1e273a; /* Change icon color on hover to dark */
  }
  &:active {
    transform: translateY(0px) scale(1); /* Reset transform on active */
  }
`;

const BottomBar = styled.div`
  text-align: center;
  margin-top: 3rem; /* Increased margin top for bottom bar */
  padding-top: 2rem; /* Padding to visually separate bottom bar */
  border-top: 1px solid #2d3748; /* Border for bottom bar */
  font-size: 0.9rem;
  color: #777;
  font-family: 'Inter', 'Manrope', 'Plus Jakarta Sans', sans-serif;
`;

const SpinnerIcon = styled(FaSpinner)`
  animation: ${spin} 1s linear infinite;
  margin-right: 0.6rem;
  font-size: 1rem;
`;

const SuccessIcon = styled(FaCheckCircle)`
  margin-right: 0.6rem;
  color: #38bdf8;
  font-size: 1.1rem;
`;

const ErrorIcon = styled(FaQuestionCircle)`
  margin-right: 0.6rem;
  color: #ff6b6b;
  font-size: 1.1rem;
`;


// --- Main Footer Component ---
const Footer = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState('');
  const [isError, setIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Track submission state for disabling button

  // Handle input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setFormStatus('');
    setIsError(false);
  };

  // Simple email validation
  const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus('All fields are required.');
      setIsError(true);
      return;
    }
    if (!isValidEmail(formData.email)) {
      setFormStatus('Please enter a valid email address.');
      setIsError(true);
      return;
    }

    setFormStatus('Sending...');
    setIsError(false);
    setIsSubmitting(true); // Disable button during submission

    try {
      const response = await axiosPublic.post('/api/0/pages/demo/request', formData);

      if (response.status === 200 || response.status === 201) {
        setFormStatus('Message sent successfully!');
        setIsError(false);
        setFormData({ name: '', email: '', message: '' });
      } else {
        const errorData = response.data;
        setFormStatus(`Failed to send message. ${errorData.message || 'Please try again.'}`);
        setIsError(true);
        console.error('Failed to send message:', response.status, errorData);
      }
    } catch (error) {
      setFormStatus('An error occurred. Please try again later.');
      setIsError(true);
      console.error('Error sending message:', error);
    } finally {
      setIsSubmitting(false); // Re-enable button after submission (success or failure)
    }
  };

  return (
    <FooterWrapper>
      <InnerContainer>
        {/* Column 1: Company Info */}
        <Column>
          <Title>About Skhool.in</Title> {/* More descriptive title */}
          <Description>
            Skhool.in is an AI-powered school administration platform designed to streamline operations and enhance the learning environment.
            We empower educational institutions to embrace digital transformation for a more efficient and engaging future.
          </Description>
        </Column>

        {/* Column 2: Quick Links (SEO-friendly internal navigation) */}
        <Column>
          <Title>Quick Links</Title>
          <List>
            <ListItem>
              <FooterLink href="/#hero">
                <LinkIcon><FaBookOpen /></LinkIcon> Home
              </FooterLink>
            </ListItem>
            <ListItem>
              <FooterLink href="/#features-section">
                <LinkIcon><FaBookOpen /></LinkIcon> Key Features
              </FooterLink>
            </ListItem>
            <ListItem>
              <FooterLink href="/#features-benefits">
                <LinkIcon><FaBookOpen /></LinkIcon> Why Skhool
              </FooterLink>
            </ListItem>
            <ListItem>
              <FooterLink href="/#testimonials">
                <LinkIcon><FaBookOpen /></LinkIcon> Testimonials
              </FooterLink>
            </ListItem>
            <ListItem>
              <FooterLink href="/pricing">
                <LinkIcon><FaFileAlt /></LinkIcon> Pricing Plans
              </FooterLink>
            </ListItem>
            <ListItem>
              <FooterLink href="/login">
                <LinkIcon><FaShieldAlt /></LinkIcon> School Login
              </FooterLink>
            </ListItem>
          </List>
        </Column>



        {/* Column 3: Resources & Support */}
        <Column>
          <Title>Resources</Title>
          <List>
            <ListItem>
              <FooterLink href="#help-center">
                <LinkIcon><FaQuestionCircle /></LinkIcon> Help Center
              </FooterLink>
            </ListItem>
            <ListItem>
              <FooterLink href="#documentation">
                <LinkIcon><FaFileAlt /></LinkIcon> Documentation
              </FooterLink>
            </ListItem>
            <ListItem>
              <FooterLink href="#tutorials">
                <LinkIcon><FaBookOpen /></LinkIcon> Tutorials
              </FooterLink>
            </ListItem>
            <ListItem>
              <FooterLink href="#faq">
                <LinkIcon><FaQuestionCircle /></LinkIcon> FAQ
              </FooterLink>
            </ListItem>
          </List>
        </Column>

        {/* Column 4: Contact Form */}
        <Column>
          <Title>Contact Us</Title>
          <ContactForm onSubmit={handleSubmit}>
            <Label htmlFor="name">Your Name</Label>
            <Input
              id="name"
              type="text"
              name="name"
              placeholder="Enter your name" /* Improved placeholder */
              value={formData.name}
              onChange={handleChange}
              aria-required="true"
              required
            />
            <Label htmlFor="email">Your Email</Label>
            <Input
              id="email"
              type="email"
              name="email"
              placeholder="Enter your email address" /* Improved placeholder */
              value={formData.email}
              onChange={handleChange}
              aria-required="true"
              required
            />
            <Label htmlFor="message">Your Message</Label>
            <Textarea
              id="message"
              name="message"
              placeholder="How can we help you?" /* Improved placeholder */
              rows="4" /* Increased rows for textarea */
              value={formData.message}
              onChange={handleChange}
              aria-required="true"
              required
            />
            <SubmitButton type="submit" disabled={isSubmitting}> {/* Disable based on isSubmitting */}
              {isSubmitting && <SpinnerIcon />}
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </SubmitButton>
            {formStatus && (
              <FormStatus error={isError}>
                <StatusIcon>
                  {isError ? <ErrorIcon /> : <SuccessIcon />} {/* Success/Error icons */}
                </StatusIcon>
                {formStatus}
              </FormStatus>
            )}
          </ContactForm>

          {/* Social Icons */}
          <SocialIconsWrapper>
            <SocialIconLink href="#">
              <FaFacebook />
            </SocialIconLink>
            <SocialIconLink href="#">
              <FaTwitter />
            </SocialIconLink>
            <SocialIconLink href="https://www.linkedin.com/company/104466915/">
              <FaLinkedin />
            </SocialIconLink>
          </SocialIconsWrapper>
        </Column>
      </InnerContainer>
      <BottomBar>
        © {new Date().getFullYear()} Skhool.in. All rights reserved. Developed by <a href="https://xpeto.in" target="_blank" rel="noreferrer">Xpeto.in</a> | <Link href="/terms">Terms of Service</Link> | <Link href="/privacy">Privacy Policy</Link>
      </BottomBar>
    </FooterWrapper>
  );
};

export default Footer;
